import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Avatar,
  IconButton,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Flex
} from '@chakra-ui/react';
import { SearchIcon, EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { fighterService } from '../../services/fighterService';
import { useDataStore } from '../../contexts/DataStoreContext';
import { FighterEditForm } from './FighterEditForm';

export const FighterManagement = () => {
  const dataStore = useDataStore();
  const { getAllFighters, getFilteredFighters, loading } = dataStore;
  const [filteredFighters, setFilteredFighters] = useState([]);
  const [selectedFighter, setSelectedFighter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [weightClassFilter, setWeightClassFilter] = useState('');
  const [fighterToDelete, setFighterToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose
  } = useDisclosure();

  const toast = useToast();


  useEffect(() => {
    filterFighters();
  }, [getAllFighters, searchTerm, weightClassFilter, loading.fighters]);

  const filterFighters = () => {
    if (loading.fighters || !getFilteredFighters) return;

    // Use DataStore's built-in filtering
    const filtered = getFilteredFighters({
      searchTerm,
      weightClass: weightClassFilter
    });

    setFilteredFighters(filtered);
  };

  const handleEditFighter = (fighter) => {
    setSelectedFighter(fighter);
    onEditModalOpen();
  };

  const handleCreateFighter = () => {
    setSelectedFighter(null);
    onEditModalOpen();
  };

  const handleFighterSaved = async () => {
    onEditModalClose();
    // DataStore will automatically update via real-time subscription
    toast({
      title: selectedFighter ? 'Fighter updated' : 'Fighter created',
      description: `Fighter has been ${selectedFighter ? 'updated' : 'created'} successfully.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDeleteClick = (fighter) => {
    setFighterToDelete(fighter);
    onDeleteModalOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!fighterToDelete) return;

    try {
      setDeleting(true);
      await fighterService.deleteFighter(fighterToDelete.id);
      // DataStore will automatically update via real-time subscription
      toast({
        title: 'Fighter deleted',
        description: `${fighterToDelete.name} has been deleted successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteModalClose();
    } catch (err) {
      toast({
        title: 'Error deleting fighter',
        description: err.message || 'Failed to delete fighter',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
      setFighterToDelete(null);
    }
  };

  const getUniqueWeightClasses = () => {
    const weightClasses = [...new Set(getAllFighters().map(f => f.weightClass).filter(Boolean))];
    return weightClasses.sort();
  };

  if (loading.fighters) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    );
  }


  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading>Fighter Management</Heading>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={handleCreateFighter}
        >
          Add New Fighter
        </Button>
      </HStack>

      {/* Filters */}
      <Card mb={6}>
        <CardBody>
          <Flex gap={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search fighters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select
              placeholder="All Weight Classes"
              maxW="200px"
              value={weightClassFilter}
              onChange={(e) => setWeightClassFilter(e.target.value)}
            >
              {getUniqueWeightClasses().map(weightClass => (
                <option key={weightClass} value={weightClass}>
                  {weightClass}
                </option>
              ))}
            </Select>
            <Text color="gray.400" alignSelf="center">
              {filteredFighters.length} of {getAllFighters().length} fighters
            </Text>
          </Flex>
        </CardBody>
      </Card>

      {/* Fighters Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {filteredFighters.map((fighter) => (
          <Card key={fighter.id} cursor="pointer" _hover={{ transform: 'translateY(-2px)' }}>
            <CardBody>
              <VStack spacing={4}>
                <Avatar
                  size="xl"
                  name={fighter.name}
                  src={fighter.photoUrl}
                  color="gray.700"
                  bg="white"
                />

                <VStack spacing={1} textAlign="center">
                  <Heading size="md">{fighter.name}</Heading>
                  {fighter.nickname && (
                    <Text fontSize="sm" color="gray.400" fontStyle="italic">
                      "{fighter.nickname}"
                    </Text>
                  )}
                </VStack>

                <HStack spacing={2} flexWrap="wrap" justify="center">
                  {fighter.weightClass && (
                    <Badge colorScheme="blue" variant="solid">
                      {fighter.weightClass}
                    </Badge>
                  )}
                  {fighter.record && (
                    <Badge colorScheme="gray" variant="outline">
                      {fighter.record}
                    </Badge>
                  )}
                </HStack>

                {fighter.gym && (
                  <Text fontSize="sm" color="gray.400" textAlign="center">
                    {fighter.gym}
                  </Text>
                )}

                <HStack spacing={2}>
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditFighter(fighter);
                    }}
                    aria-label="Edit fighter"
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(fighter);
                    }}
                    aria-label="Delete fighter"
                  />
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {filteredFighters.length === 0 && (
        <Card>
          <CardBody textAlign="center" py={10}>
            <Text color="gray.400" mb={4}>
              {getAllFighters().length === 0
                ? 'No fighters created yet'
                : 'No fighters match your search criteria'
              }
            </Text>
            {getAllFighters().length === 0 && (
              <Button colorScheme="blue" onClick={handleCreateFighter}>
                Create Your First Fighter
              </Button>
            )}
          </CardBody>
        </Card>
      )}

      {/* Edit/Create Fighter Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedFighter ? 'Edit Fighter' : 'Create New Fighter'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FighterEditForm
              fighter={selectedFighter}
              onSave={handleFighterSaved}
              onCancel={onEditModalClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Fighter</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="start" spacing={4}>
              <Text fontSize="md" color="white">
                Are you sure you want to delete{' '}
                <Text as="span" fontWeight="bold" color="red.400">
                  {fighterToDelete?.name}
                </Text>
                ?
              </Text>
              <Text fontSize="sm" color="gray.400">
                This action cannot be undone. The fighter will be permanently removed from all events and fights.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose} isDisabled={deleting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteConfirm}
              isLoading={deleting}
              loadingText="Deleting..."
            >
              Delete Fighter
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
